import { useState } from 'react';
import type { Layout } from 'react-grid-layout';
import { formatHours, percentOf } from '../charts/chart-theme';
import { KpiStrip } from '../charts/kpi-strip';
import { DashboardGrid } from '../dashboard-grid';
import { DashboardShell } from '../dashboard-shell';
import { DRILL_KEY } from '../drill-layout';
import { DrillPanel, type DrillTarget } from '../drill-panel';
import { formatPeriodLong } from '../period-picker';
import type { DashboardViewProps } from '../registry';
import { useDashboardLayout } from '../use-dashboard-layout';
import { CapacityTile } from './capacity-tile';
import { EngagementTile } from './engagement-tile';
import {
  ALL_PEOPLE,
  ENGAGEMENTS,
  FIRM,
  type Group,
  KPIS,
  LOCATIONS,
  type Person,
  peopleFor,
  SEGMENTS,
} from './fixtures';
import { MixTile } from './mix-tile';
import {
  PEOPLE_COHORT_SEGMENTS,
  PEOPLE_COLUMNS,
  peopleCohorts,
  peopleSearchText,
} from './people-drill';
import { RoleMismatchTile } from './role-mismatch-tile';

/**
 * Default tile arrangement. `h` is in 30px grid rows and is sized to each
 * tile's content — tiles never scroll, so a tile that grows gets a taller `h`
 * rather than an inner scrollbar.
 */
const BASE_LAYOUT: Layout = [
  { i: 'mixLocation', x: 0, y: 0, w: 24, h: 10, minW: 12, minH: 8 },
  { i: 'mixSegment', x: 0, y: 10, w: 24, h: 9, minW: 12, minH: 7 },
  { i: 'hourly', x: 0, y: 19, w: 24, h: 10, minW: 12, minH: 8 },
  { i: 'retainer', x: 0, y: 29, w: 24, h: 10, minW: 12, minH: 8 },
  { i: 'capacity', x: 0, y: 39, w: 24, h: 13, minW: 12, minH: 10 },
  { i: 'mismatch', x: 0, y: 52, w: 24, h: 10, minW: 12, minH: 9 },
];

/** How many synthetic people a group drilldown shows. */
const DRILL_SAMPLE = 12;

export function UtilizationDashboard({ dashboard, period, onPeriodChange }: DashboardViewProps) {
  const [drill, setDrill] = useState<DrillTarget<Person> | null>(null);
  const { layout, onLayoutChange, reset, dirty } = useDashboardLayout({
    baseLayout: BASE_LAYOUT,
    drillSource: drill?.source ?? null,
  });

  const drillGroup = (group: Group, source: string, sourceLabel: string): void => {
    const shown = Math.min(group.people, DRILL_SAMPLE);
    setDrill({
      source,
      sourceLabel,
      title: group.name,
      note: `${group.people} people · showing ${shown}`,
      rows: peopleFor(group, shown).map((p) => ({ ...p, location: group.name })),
    });
  };

  const drillFirm = (source: string, sourceLabel: string, title: string, note: string): void => {
    setDrill({ source, sourceLabel, title, note, rows: ALL_PEOPLE });
  };

  return (
    <DashboardShell
      dashboard={dashboard}
      period={period}
      summary={`${formatPeriodLong(period)} · ${FIRM.people} people · ${formatHours(FIRM.committed)} committed hrs`}
      onPeriodChange={onPeriodChange}
      onResetLayout={reset}
      layoutDirty={dirty}
    >
      <KpiStrip items={KPIS} />

      <DashboardGrid layout={layout} onLayoutChange={onLayoutChange}>
        <div key="mixLocation">
          <MixTile
            title="Utilization Mix — by Location"
            groups={LOCATIONS}
            groupLabel="Location"
            onDrill={(g) => drillGroup(g, 'mixLocation', 'Utilization Mix — by Location')}
            insights={{
              footnote: "Generated from this month's timesheet data",
              bullets: [
                <>
                  <b>Overall utilization is {percentOf(FIRM.committed, FIRM.available)}%</b> of
                  net-available capacity across {FIRM.people} people.
                </>,
                <>
                  <b>Bangalore is the anchor</b> —{' '}
                  {percentOf(LOCATIONS[0]?.available ?? 0, FIRM.available)}% of all available hours,
                  and heavily retainer-weighted.
                </>,
                <>
                  <b>USA is the most hourly-weighted location</b> and is running above its available
                  hours — a capacity risk if leave lands in the same month.
                </>,
                <>
                  <b>Chandigarh and Udupi carry the most idle</b> — the clearest place to redeploy
                  capacity.
                </>,
              ],
            }}
          />
        </div>

        <div key="mixSegment">
          <MixTile
            title="Utilization Mix — by Segment"
            groups={SEGMENTS}
            groupLabel="Segment"
            onDrill={(g) => drillGroup(g, 'mixSegment', 'Utilization Mix — by Segment')}
            insights={{
              footnote: "Generated from this month's timesheet data",
              bullets: [
                <>
                  <b>ACT + MDG carries the book</b> — {formatHours(SEGMENTS[0]?.available ?? 0)} of{' '}
                  {formatHours(FIRM.available)} available hrs.
                </>,
                <>
                  <b>Ops is almost entirely internal</b> — msCFO work is{' '}
                  {percentOf(SEGMENTS[2]?.internal ?? 0, SEGMENTS[2]?.available ?? 1)}% of its
                  capacity, with very little client-billable work.
                </>,
                <>
                  <b>
                    GL runs {percentOf(SEGMENTS[3]?.internal ?? 0, SEGMENTS[3]?.available ?? 1)}%
                    internal
                  </b>{' '}
                  — alongside Ops, the clearest pool of hours to review for client conversion.
                </>,
                <>
                  <b>Exiting has the highest idle share</b> — expected during off-boarding, but
                  worth confirming.
                </>,
              ],
            }}
          />
        </div>

        {ENGAGEMENTS.map((engagement) => (
          <div key={engagement.key}>
            <EngagementTile
              engagement={engagement}
              locations={LOCATIONS}
              segments={SEGMENTS}
              onDrillGroup={(g) => drillGroup(g, engagement.key, engagement.name)}
              onDrillFirm={() =>
                drillFirm(
                  engagement.key,
                  engagement.name,
                  `${engagement.name} — contributing people`,
                  `firm-wide population booking ${engagement.name.toLowerCase()} hours`,
                )
              }
              insights={{
                footnote: 'Billability is the entry flag; engagement type is the customer type',
                bullets: [
                  <>
                    <b>{formatHours(engagement.billable + engagement.nonBillable)} hrs</b> booked
                    this month — ~
                    {percentOf(engagement.billable + engagement.nonBillable, FIRM.available)}% of
                    net-available capacity.
                  </>,
                  <>
                    <b>
                      {percentOf(engagement.billable, engagement.billable + engagement.nonBillable)}
                      % billable
                    </b>{' '}
                    — the {formatHours(engagement.nonBillable)} non-billable hrs are the servicing
                    overhead to watch.
                  </>,
                  <>
                    <b>Group by location or segment</b> to see which pools carry this engagement
                    type, then click a bar to drill into its people.
                  </>,
                ],
              }}
            />
          </div>
        ))}

        <div key="capacity">
          <CapacityTile
            onDrill={(step) =>
              drillFirm(
                'capacity',
                'Capacity',
                `${step} — contributing people`,
                'firm-wide population',
              )
            }
            insights={{
              footnote: 'Idle = net-available − committed · leave and holiday are already excluded',
              bullets: [
                <>
                  <b>Idle is the biggest lever</b> —{' '}
                  {formatHours(
                    FIRM.available - FIRM.billable - FIRM.nonBillable - FIRM.free - FIRM.internal,
                  )}{' '}
                  hrs of unspent capacity.
                </>,
                <>
                  <b>Client-billable is the largest draw</b> at {formatHours(FIRM.billable)} hrs (~
                  {percentOf(FIRM.billable, FIRM.available)}% of capacity).
                </>,
                <>
                  <b>Internal msCFO consumes {percentOf(FIRM.internal, FIRM.available)}%</b> (
                  {formatHours(FIRM.internal)} hrs) — concentrated in Ops &amp; GL.
                </>,
              ],
            }}
          />
        </div>

        <div key="mismatch">
          <RoleMismatchTile
            onDrillGroup={(label, people) =>
              setDrill({
                source: 'mismatch',
                sourceLabel: 'Role Mismatch',
                title: `${label} — off-level hours`,
                note: "hours booked at a level other than the person's designated one",
                rows: people,
              })
            }
            onDrillAll={(people) =>
              setDrill({
                source: 'mismatch',
                sourceLabel: 'Role Mismatch',
                title: 'All off-level hours',
                note: `${people.length} people with hours booked away from their designated level`,
                rows: people,
              })
            }
            insights={{
              footnote:
                "Mismatch = the entry's service role ≠ the employee's designated service role",
              bullets: [
                <>
                  <b>Hours booked below level</b> mean higher-cost people doing lower-level work —
                  the direction that erodes margin.
                </>,
                <>
                  <b>Hours booked above level</b> are stretch work: useful for development, a
                  delivery-risk signal if sustained.
                </>,
                <>
                  <b>Both directions are computable from the timesheet report today</b> — it already
                  carries the entry's service role and the employee's designated role.
                </>,
              ],
            }}
          />
        </div>

        {drill ? (
          <div key={DRILL_KEY}>
            <DrillPanel
              target={drill}
              columns={PEOPLE_COLUMNS}
              searchText={peopleSearchText}
              cohorts={peopleCohorts}
              cohortSegments={PEOPLE_COHORT_SEGMENTS}
              unit="people"
              onClose={() => setDrill(null)}
            />
          </div>
        ) : null}
      </DashboardGrid>
    </DashboardShell>
  );
}
